import React from 'react';
import PropTypes from 'prop-types';

const ModalViewContext = React.createContext(undefined);

// Actions
export const SHOW_MODAL = 'show-modal';
export const HIDE_MODAL = 'hide-modal';

const init = () => {
  return {
    showModal: null,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case SHOW_MODAL: {
      return {
        ...state,
        showModal: action.modalName,
      };
    }
    case HIDE_MODAL: {
      return {
        ...state,
        showModal: null,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
};

const ModalViewProvider = ({
  children,
}) => {
  const [state, dispatch] = React.useReducer(reducer, null, init);

  const handleShowModal = React.useCallback(async (modalName) => {
    dispatch({ type: SHOW_MODAL, modalName });
  }, []);

  const handleHideModal = React.useCallback(() => {
    dispatch({ type: HIDE_MODAL });
  }, []);

  const memoizedValue = React.useMemo(() => {
    return {
      isModalOpen: (name) => {
        return name === state.showModal;
      },
      handleShowModal,
      handleHideModal,
    };
  }, [
    state,
    handleShowModal,
    handleHideModal,
  ]);

  return (
    <ModalViewContext.Provider value={memoizedValue}>
      {children}
    </ModalViewContext.Provider>
  );
};

// Use Survey State Context
const useModalViewContext = () => {
  const context = React.useContext(ModalViewContext);
  if (context === undefined) {
    throw new Error('useModalViewContext must be used within a ModalViewProvider');
  }
  return context;
};

ModalViewProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  ModalViewProvider,
  useModalViewContext,
};
